import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useApi } from "../api/ApiContext";
import { ApiError } from "../api/errors";
import type { AccountPartyType, MeResponse, PartyType } from "../api/types";

// Session model (see web/README.md "Auth"):
//
//  * The session is the httpOnly `vads_session` cookie that POST
//    /v1/auth/token sets (auth.go:213). JS never reads it.
//  * The CSRF token from that same response is kept in sessionStorage
//    (api/client.ts sessionCsrfStore). A tab without one (a new tab, a
//    restored tab) asks GET /v1/auth/session for it on load (G-02,
//    cmd/core-platform/account.go:144-155): the token is the session's
//    own and stable, so every tab shares it. A 401 there means there's
//    no session, and the tab is signed out.
//  * Who the user is comes from GET /v1/me (G-01, account.go:66-126):
//    party type (which portal), email, and MFA status — whether a factor
//    is enrolled (the RequireMfa gate) and whether this session's MFA is
//    fresh (step-up hints). It's re-read after enrolling a factor and
//    after a step-up, so neither is guessed.

export type MfaEnrollment = "enrolled" | "not_enrolled";

export type AuthState =
  | { status: "loading" }
  | { status: "signed_out"; reason?: string }
  | { status: "signed_in"; me: MeResponse; partyType: PartyType; mfa: MfaEnrollment }
  /** A staff (platform party) session: the staff console at /staff (auth/guards.tsx RequireStaff). */
  | { status: "platform"; me: MeResponse };

interface AuthContextValue {
  state: AuthState;
  /** Called by the login flow after a successful token exchange. */
  completeLogin: (args: { csrfToken: string; mfaEnrolled: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-read /v1/me: after enrolling a factor, or completing a step-up. */
  refreshMe: () => Promise<void>;
  /** A factor was just enrolled: open the gate now, and confirm from /v1/me. */
  markMfaEnrolled: () => void;
  /**
   * The signed-in party is closed (migration 0065). Set by any 403
   * account_closed (ApiClient.setAccountClosedHandler) or by a successful
   * closure; /v1/me doesn't say, so the first refused call is how a new
   * session learns it. The shell then shows only the closed-account screen
   * and the statements (layout/ClosedAccount.tsx).
   */
  accountClosed: boolean;
  markAccountClosed: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function stateFor(me: MeResponse): AuthState {
  if (me.party_type === "platform") return { status: "platform", me };
  return { status: "signed_in", me, partyType: me.party_type, mfa: me.mfa.enrolled ? "enrolled" : "not_enrolled" };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { client, endpoints } = useApi();
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [accountClosed, setAccountClosed] = useState(false);

  const clearLocal = useCallback(() => {
    client.csrf.set(null);
    queryClient.clear();
    setAccountClosed(false);
  }, [client, queryClient]);

  const loadSession = useCallback(
    async (refetchCsrf: boolean): Promise<AuthState> => {
      try {
        if (refetchCsrf) {
          // G-02: on load, ask for the token of the session the cookie names.
          // A new or restored tab has none; a tab whose token belongs to an
          // older session (signed in again elsewhere) gets the current one.
          try {
            const { csrf_token } = await endpoints.auth.session();
            client.csrf.set(csrf_token);
          } catch (err) {
            // A 401 is "no session" (handled below). Anything else (network,
            // 5xx) keeps a token this tab already has; with none, it's fatal.
            if (!(err instanceof ApiError) || err.kind === "session" || err.kind === "csrf" || !client.csrf.get()) throw err;
          }
        }
        return stateFor(await endpoints.auth.me());
      } catch (err) {
        if (err instanceof ApiError && (err.kind === "session" || err.kind === "csrf")) {
          clearLocal();
          // Plain "not signed in": the sign-in page needs no explanation.
          return { status: "signed_out" };
        }
        if (err instanceof ApiError && err.code === "cross_origin_forbidden") {
          clearLocal();
          return { status: "signed_out", reason: err.message };
        }
        throw err;
      }
    },
    [client, endpoints, clearLocal],
  );

  useEffect(() => {
    let cancelled = false;
    loadSession(true)
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch(() => {
        if (!cancelled) setState({ status: "signed_out", reason: "We couldn't reach VADS to check your session." });
      });
    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  // Any 401 / csrf_mismatch anywhere drops the app to the sign-in screen.
  useEffect(() => {
    client.setSessionLostHandler((err) => {
      clearLocal();
      setState({ status: "signed_out", reason: err.message });
    });
    return () => client.setSessionLostHandler(null);
  }, [client, clearLocal]);

  // Any 403 account_closed anywhere switches the portal to its closed state.
  const markAccountClosed = useCallback(() => setAccountClosed(true), []);
  useEffect(() => {
    client.setAccountClosedHandler(markAccountClosed);
    return () => client.setAccountClosedHandler(null);
  }, [client, markAccountClosed]);

  const completeLogin = useCallback(
    async ({ csrfToken }: { csrfToken: string; mfaEnrolled: boolean }) => {
      queryClient.clear();
      setAccountClosed(false);
      client.csrf.set(csrfToken);
      // The token exchange just returned this session's token.
      setState(await loadSession(false));
    },
    [client, loadSession, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await endpoints.auth.logout();
    } catch {
      // The session may already be gone; either way this tab forgets it.
    }
    clearLocal();
    setState({ status: "signed_out", reason: "You've been signed out." });
  }, [endpoints, clearLocal]);

  const refreshMe = useCallback(async () => {
    try {
      const me = await endpoints.auth.me();
      setState((s) => (s.status === "signed_in" || s.status === "platform" ? stateFor(me) : s));
    } catch {
      // A lost session is handled by the session-lost handler; anything
      // else leaves the last known state in place.
    }
  }, [endpoints]);

  const markMfaEnrolled = useCallback(() => {
    setState((s) =>
      s.status === "signed_in"
        ? { ...s, mfa: "enrolled", me: { ...s.me, mfa: { ...s.me.mfa, enrolled: true } } }
        : s.status === "platform"
          ? { ...s, me: { ...s.me, mfa: { ...s.me.mfa, enrolled: true } } }
          : s,
    );
    void refreshMe();
  }, [refreshMe]);

  const value = useMemo(
    () => ({ state, completeLogin, logout, refreshMe, markMfaEnrolled, accountClosed, markAccountClosed }),
    [state, completeLogin, logout, refreshMe, markMfaEnrolled, accountClosed, markAccountClosed],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** For components that may render outside <AuthProvider> (the step-up dialog in isolation). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

/**
 * For screens shared by the portals and the staff console (Sign-in &
 * security): any signed-in account, customer or staff.
 */
export function useAccount(): { me: MeResponse; partyType: AccountPartyType; mfa: MfaEnrollment } {
  const { state } = useAuth();
  if (state.status === "signed_in") return { me: state.me, partyType: state.partyType, mfa: state.mfa };
  if (state.status === "platform") return { me: state.me, partyType: "platform", mfa: state.me.mfa.enrolled ? "enrolled" : "not_enrolled" };
  throw new Error("useAccount used outside a signed-in route");
}

/** For screens rendered only inside a signed-in route tree. */
export function useSession(): Extract<AuthState, { status: "signed_in" }> {
  const { state } = useAuth();
  if (state.status !== "signed_in") throw new Error("useSession used outside a signed-in route");
  return state;
}
